/**
 * Passenger band step: 4 giant tiles, values from the registry
 * (/api/options bands — the triple-source dies here). Band names are
 * net-locked ('1-4' etc.) and arrive from the server verbatim.
 *
 * ⛔⛔ AND ONE FORWARD EDGE FOR A PARTY NO TILE FITS (T1871 M5, board #1871).
 * Until this milestone the four tiles were the ONLY transition off this screen,
 * so a lead for 17 people — which the router correctly sends here, because '16+'
 * is not a band the engine can price — had no forward exit at all. Helen could
 * not reach a screen where she could hand-price and save; her only moves were to
 * record a band she knew understated the party, or to abandon the job. That is
 * the reported defect, and it is entirely client-side: four server doors already
 * admit the over-band token and `bookings.passengers_band` has no CHECK.
 *
 * ⛔ THE ROUTER IS NOT THE WALL AND `furthestReadyScreen` IS NOT TOUCHED.
 * `store.spec.ts` pins an over-band lead to 'passengers' in all three pricing
 * modes and `e2e/tests/green/lead-prefill-carry.spec.ts` pins the same on the
 * served kiosk. The screen was the defect, so the screen is what changed: an
 * unanswered '16+' lead still lands here with nothing selected, and HELEN'S ACT
 * — pressing the edge below — is what makes it a deliberate over-band job.
 */
// ⛔ THE ENGINE'S TOKEN, NEVER A LOCAL SPELLING. `check-copy.sh`'s enumeration
// rule bans the four PRICED band literals and not this one; its T1871 M6
// `scan_over_band` arm bans THIS one everywhere but `engine/bands.ts`. Between
// them a hand-typed copy of either kind now reds instead of quietly becoming a
// second copy of a fact that must live once.
import { OVER_BAND, bandForCount } from '../../engine/bands.js';
import { Banner } from '../components/Banner.js';
import { bands, type Registry } from '../store/registry.js';

export type PassengersProps = {
  registry: Registry;
  /** The band carried from the lead, so a prefilled pick renders as SELECTED
   *  (the incumbent had this; the rebuild dropped it). A band the registry
   *  does not know (e.g. the form's '16+') matches no tile — correct: the
   *  router sends it here precisely so Helen picks a real one, or takes the
   *  over-band edge below. */
  band: string | null;
  /**
   * ⛔⛔ THE REAL HEADCOUNT, AND P0-3'S TRIGGER IS A DISJUNCTION (T1871 M5,
   * BLOCK). P0-3 reads "a count above 16 (OR a `16+` band from an older
   * inquiry)". M5 shipped only the parenthetical, so a fresh 22-passenger job
   * whose band was tapped to `12-16` got a fully computed grid price with no
   * warning anywhere — Helen's ORIGINAL report ("bookings arrive as 12-16 with
   * far more people") recreated on the screen built to fix it.
   *
   * ⛔ THE VALUE, NEVER THE PRESENCE. The standing Captain delta forbids gating
   * on whether a count EXISTS; reacting to what it SAYS is fine. So `null`
   * behaves exactly as it did before this prop existed — it falls back to the
   * band and changes nothing — and there is no `Number(...)` and no truthiness
   * anywhere near it (`Number(null) === 0` would read "not captured" as a party
   * of nobody).
   *
   * ⛔ AND IT IS `bandForCount`, NEVER A BARE `> 16`. The threshold is the
   * engine's table and lives once; a literal here would be a second copy of the
   * boundary that `bands.spec.ts` grades ([16, '12-16'] and [17, '16+'] are
   * adjacent rows in it).
   */
  passengersExact: number | null;
  onPick: (band: string) => void;
};

export function Passengers({ registry, band, passengersExact, onPick }: PassengersProps) {
  /**
   * The count's OWN verdict, or null when there is nothing to ask. Three
   * outcomes and they are not two: the over-band token, a priced band, and
   * `null` for a count the engine has no band for at all (a typo, or a value
   * past the server's ceiling) — which is NOT a contradiction of anything and
   * must not be read as one.
   */
  const derived = passengersExact == null ? null : bandForCount(passengersExact);
  /** The band says over-band, OR the count does. P0-3's disjunction, verbatim. */
  const overBand = band === OVER_BAND || derived === OVER_BAND;
  /**
   * ⛔ THE ONE THING THAT HIDES THE FORWARD EDGE: a captured count that
   * positively lands in a PRICED band. Reconciles the two reviewers who
   * disagreed about the edge — security-review measured that an unconditional
   * edge lets one press put `band='16+'` on a 4-passenger job AND PIN IT, after
   * which the headcount can no longer correct it back; ux-review is right that a
   * walk-in with no count still needs the edge. A count of 4 contradicts it; a
   * count of 22 agrees with it; NO count (the overwhelmingly common case, and
   * the only one on a walk-in) contradicts nothing.
   */
  const countFitsAPricedBand = derived !== null && derived !== OVER_BAND;
  return (
    <div class="screen-pane">
      <h1 class="screen-title" tabIndex={-1}>How many passengers?</h1>
      <div class="tile-grid tile-grid--bands">
        {bands(registry).map((b) => {
          const selected = b === band;
          return (
            <button
              key={b}
              type="button"
              class={`tile tile--band${selected ? ' tile--selected' : ''}`}
              aria-pressed={selected}
              onClick={() => onPick(b)}
            >
              <span class="tile-label tile-label--big">{b}</span>
            </button>
          );
        })}
      </div>

      {/* ⛔ THE STATE, AND IT COSTS ZERO CONTROLS. `Banner` renders a
          `<div role="status">`, which matches none of the selectors
          control-budget.spec.tsx counts — a `<span>`, a `<p>` and a Banner are
          all free there, while any button/input/link costs 1. It says what is
          true (no tile fits, this one is priced by hand) instead of nagging
          Helen to pick a band that would understate the party.
          ⛔ ONE SENTENCE, BOTH ARMS OF THE DISJUNCTION, and deliberately not a
          second string for the count arm: "over 16 passengers, no band fits" is
          equally true whether the OVER-BAND TOKEN says so or the COUNT does, and
          a near-duplicate sentence beside it would read as one stuttering
          message (the hazard BandControl's two banners already record).
          ⛔ IT DOES NOT BLOCK ANYTHING, AND THAT IS STILL TRUE — BUT THE
          JUSTIFICATION HAS CHANGED. The four tiles stay live and stay pressable
          at 22 passengers. What is no longer true is the sentence that used to
          close this note, "whether to REFUSE that pairing is a product question
          with Alex": board ask 805 answered it, "Refuse disagreement at the save
          door". The refusal is a 400 at the three save doors (server/app.ts),
          NEVER a disabled control here - a screen that greys out the tile Helen
          means to press teaches her nothing, while the door names the field.
          ⚠️ SO THE PAIRING IS NOW PRICEABLE AND UNSAVEABLE: she can still take
          the two-minibus price for a party of 22 on `12-16` and cannot store it.
          That is what was ruled; the shape that would let her (a vehicle count,
          or an explicit per-vehicle marker) is a design question for Alex and
          Vicky and is on the record as a residual, not something to invent here.
          ⚠️ ASCII HYPHEN, never an em dash: check-copy.sh scans all of
          src/client for the character. */}
      {overBand && (
        <Banner kind="warn">
          Over 16 passengers - no band fits, so this job is priced by hand.
        </Banner>
      )}

      {/* ⛔⛔ THE FORWARD EDGE — READ ALL FOUR CONSTRAINTS BEFORE CHANGING IT.
          (1) It carries NO `aria-pressed`. `passengers.spec.tsx` counts
              `button[aria-pressed]` against the registry's band list, so a fifth
              pressed button reds a green spec — and it is not a band toggle
              anyway, it is a transition.
          (2) Its accessible name is NOT a band token. The e2e net names the four
              bands on this screen and `quote-edit-in-place.spec.tsx` pins the
              ABSENCE of a '16+' chip on Quote; a control labelled with the token
              would collide with both.
          (3) It sits OUTSIDE `.tile-grid--bands`, deliberately. That grid is
              `repeat(4, 1fr)` and its own CSS comment records why: the bands are
              an ORDERED SCALE read left to right, and "a registry that ever
              served a fifth band would orphan again". A fifth tile inside it
              would drop onto a second row on its own. `.action-row` + `.btn` is
              the shipped shape for exactly this, so no new pattern is minted and
              board #1749 is untouched.
              ⚠️ CITATION CORRECTED (T1871 M5 review): this said "Location.tsx",
              which does not contain the shape at all — it uses `.btn-row`. The
              real precedents are Quote.tsx (its own "Override price" row),
              Done.tsx and BookingDetail.tsx. The substantive claim was true; the
              file named was not. Re-derive with:
              `/usr/bin/grep -c action-row src/client/screens/Quote.tsx`.
          (4) It reuses `onPick`, the SAME handler the tiles use, so the band
              write and the advance stay welded together in App.tsx rather than
              being re-implemented here. The four PRICED tiles are unchanged;
              there is no fifth priced band. */}
      {!countFitsAPricedBand && (
        <div class="action-row">
          <button
            type="button"
            class="btn btn--secondary"
            onClick={() => onPick(OVER_BAND)}
          >
            More than 16 - price by hand
          </button>
        </div>
      )}
    </div>
  );
}
